import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { connectedPlatforms } from '@/lib/athleteScore'
import SetupShell, { SetupCard, SectionTitle } from '@/components/setup/SetupShell'
import PlatformIcon from '@/components/PlatformIcon'
import { cn } from '@/lib/utils'
import { TPP_LOGO_URL } from '@adthlete/common'

const PLATFORMS = [
  {
    id: 'instagram',
    name: 'Instagram',
    proves: 'Followers, engagement, posting rhythm',
    to: '/connect/instagram',
  },
  { id: 'tiktok', name: 'TikTok', proves: 'Views, completion rate, reach', to: '/connect/tiktok' },
  { id: 'youtube', name: 'YouTube', proves: 'Subscribers, watch time', to: '/connect/youtube' },
] as const

const ConnectAccounts: React.FC = () => {
  const navigate = useNavigate()
  const { athlete } = useAuth()
  const connected = new Set(connectedPlatforms(athlete ?? {}))
  const anyConnected = connected.size > 0

  return (
    <SetupShell
      title="Connect accounts"
      subtitle={
        <div>
          <SectionTitle>Prove your numbers</SectionTitle>
          <p className="mt-1 text-sm text-gray-600">
            Brands trust verified numbers. Each account you connect backs up your prices.
          </p>
        </div>
      }
      footer={
        <Button
          variant={anyConnected ? 'lime' : 'outline'}
          className={cn(
            'h-12 w-full text-base',
            !anyConnected &&
              'border-foreground/20 bg-transparent text-foreground hover:bg-foreground/10',
          )}
          onClick={() => navigate('/brand-view')}
        >
          {anyConnected ? 'Back to your Content Shop' : 'Skip for now'}
        </Button>
      }
      footnote={
        anyConnected
          ? 'Your numbers update the moment an account connects.'
          : 'Your numbers update the moment an account connects. You can connect later from your Content Shop.'
      }
    >
      <ul className="space-y-2">
        {PLATFORMS.map((p) => {
          const isConnected = connected.has(p.id)
          return (
            <li
              key={p.id}
              className="flex items-center gap-3 rounded-2xl border border-foreground/10 bg-card p-3.5 shadow-[0_1px_2px_rgba(30,41,59,0.04)]"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
                <PlatformIcon platform={p.id} className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-foreground">{p.name}</span>
                <span className="block text-xs text-gray-600">{p.proves}</span>
              </span>
              {isConnected ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#C0FD72] px-3 py-1.5 text-xs font-bold text-black">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} /> Connected
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full border-foreground/25 bg-transparent text-foreground hover:bg-foreground/10"
                  onClick={() => navigate(p.to)}
                >
                  Connect
                </Button>
              )}
            </li>
          )
        })}
      </ul>

      <SetupCard className="flex items-center gap-3">
        <div className="flex h-12 w-24 shrink-0 items-center justify-center rounded-lg bg-[#FFFFFF] p-1">
          <img
            src={TPP_LOGO_URL}
            alt="Talent Pitch Pro"
            className="h-full w-full object-contain"
          />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Powered by Talent Pitch Pro</p>
          <p className="mt-1 text-xs leading-relaxed text-gray-600">
            We use our sister company, <span className="text-[#5DBED9]">Talent Pitch Pro</span>, to
            securely connect your social accounts. When you connect a platform, you will be
            redirected to their verification page.
          </p>
        </div>
      </SetupCard>

      <SetupCard>
        <h3 className="font-bold text-foreground">What connecting gives you</h3>
        <p className="mt-1 text-sm text-gray-600">
          Verified followers and engagement on your Content Shop, so brands book with confidence.
          We also use them to match you with brands that fit your audience.
        </p>
      </SetupCard>
    </SetupShell>
  )
}

export default ConnectAccounts
